/**
 * Stores Report Tab Component
 *
 * Component for displaying marketer stores report
 */

import React, {useEffect} from 'react';
import {Marketer, MarketerStoresReport} from '../models/report.model';
import {useReports} from '../hooks/useReports';
import {InlineLoadingState} from '../../../Presentation/components/loading/InlineLoadingState';
import {FinancialSummaryCard} from './FinancialSummaryCard';
import {StoresTable} from './StoresTable';
import {ProductsTable} from './ProductsTable';
import {StockTable} from './StockTable';
import {AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';

interface StoresReportTabProps {
  marketer: Marketer;
}

const StoresReportContent: React.FC<{storesReport: MarketerStoresReport}> = ({storesReport}) => {
  // Export configuration for stores
  const {createConfig: createStoresConfig} = useExportConfig<any>();
  const storesExportConfig = createStoresConfig([
    {key: 'store_name', title: 'اسم المتجر'},
    {key: 'invoice_value', title: 'قيمة الفاتورة', formatter: ExportFormatters.currencySARExport},
    {key: 'paid_amount', title: 'المدفوع', formatter: ExportFormatters.currencySARExport},
    {key: 'remaining_balance', title: 'المتبقي', formatter: ExportFormatters.currencySARExport},
    {
      key: 'payment_status',
      title: 'حالة الدفع',
      formatter: (value: any) => {
        const statusMap = {
          paid: 'مدفوع',
          partial: 'مدفوع جزئياً',
          unpaid: 'غير مدفوع'
        };
        return statusMap[value as keyof typeof statusMap] || 'غير محدد';
      }
    },
    {
      key: 'last_payment_date',
      title: 'آخر دفع',
      formatter: (value: any) => {
        const date = new Date(value as string);
        return date.toLocaleString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          calendar: 'gregory',
          hour12: true
        });
      }
    }
  ], 'البيانات المالية للمتاجر', 'تصدير البيانات المالية للمتاجر', 'تصدير قائمة المتاجر مع تفاصيلها المالية');

  // Export configuration for products
  const {createConfig: createProductsConfig} = useExportConfig<any>();
  const productsExportConfig = createProductsConfig([
    {key: 'product_name', title: 'اسم المنتج'},
    {key: 'quantity_sold', title: 'الكمية المباعة'},
    {key: 'unit_price', title: 'سعر الوحدة', formatter: ExportFormatters.currencySARExport},
    {key: 'total_revenue', title: 'إجمالي الإيرادات', formatter: ExportFormatters.currencySARExport}
  ], 'مبيعات المنتجات', 'تصدير بيانات مبيعات المنتجات', 'تصدير قائمة المنتجات مع تفاصيل المبيعات');

  // Export hooks
  const storesExport = useTableExport({
    data: storesReport.stores_financial,
    config: storesExportConfig
  });

  const productsExport = useTableExport({
    data: storesReport.product_sales,
    config: productsExportConfig
  });

  return (
    <div className="space-y-6">
      <FinancialSummaryCard summary={storesReport.summary} />

      {/* Stores Table Card */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <AppCardTitle className="text-gray-900">البيانات المالية للمتاجر</AppCardTitle>
            <ExportButton
              onExport={storesExport.exportData}
              isExporting={storesExport.isExporting}
              canExport={storesExport.canExport}
              recordCount={storesExport.recordCount}
              showDropdown={true}
            />
          </div>
        </div>
        <StoresTable stores={storesReport.stores_financial} />
      </Card>

      {/* Products Table Card */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <AppCardTitle className="text-gray-900">مبيعات المنتجات</AppCardTitle>
            <ExportButton
              onExport={productsExport.exportData}
              isExporting={productsExport.isExporting}
              canExport={productsExport.canExport}
              recordCount={productsExport.recordCount}
              showDropdown={true}
            />
          </div>
        </div>
        <ProductsTable products={storesReport.product_sales} />
      </Card>
    </div>
  );
};

export const StoresReportTab: React.FC<StoresReportTabProps> = ({marketer}) => {
  const {
    storesReport,
    stockReport,
    isLoadingStoresReport,
    isLoadingStockReport,
    error,
    getMarketerStoresReport,
    getMarketerStockReport,
    filters
  } = useReports();

  // Stock export configuration (declare hooks unconditionally)
  const {createConfig: createStockConfig} = useExportConfig<any>();
  const stockExportConfig = createStockConfig([
    {key: 'product_name', title: 'اسم المنتج'},
    {key: 'product_sku', title: 'باركود'},
    {key: 'brand_name', title: 'العلامة التجارية'},
    {key: 'quantity_available', title: 'الكمية المتاحة'}
  ], 'تقرير مخزون المسوق', 'تصدير تقرير المخزون', 'تصدير بيانات مخزون المسوق');

  const stockExport = useTableExport({
    data: stockReport?.items || [],
    config: stockExportConfig
  });

  useEffect(() => {
    getMarketerStoresReport(marketer.id, filters).catch(() => {
      // Error is handled by the hook and shown via ErrorContext
    });
    getMarketerStockReport(marketer.id, {...filters, only_with_stock: true}).catch(() => {
      // Error is handled by the hook
    });
  }, [marketer.id, filters, getMarketerStoresReport, getMarketerStockReport]);

  if (isLoadingStoresReport) {
    return <InlineLoadingState message="جاري تحميل تقرير المتاجر..." />;
  }

  // Error is handled by StandardizedErrorHandler and shown as toast notification
  // No need to display inline error - just show empty state if no data
  if (!storesReport) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">لا توجد بيانات متاحة</p>
      </div>
    );
  }

  return (
    <>
      <StoresReportContent storesReport={storesReport} />

      {/* Stock Table Card */}
      <Card className="mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <AppCardTitle className="text-gray-900">مخزون المسوق</AppCardTitle>
            <ExportButton
              onExport={stockExport.exportData}
              isExporting={stockExport.isExporting}
              canExport={stockExport.canExport}
              recordCount={stockExport.recordCount}
              showDropdown={true}
            />
          </div>
        </div>
        {isLoadingStockReport ? (
          <div className="p-6"><InlineLoadingState message="جاري تحميل تقرير المخزون..." /></div>
        ) : stockReport && stockReport.items.length > 0 ? (
          <div className="p-0">
            <StockTable items={stockReport.items} />
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">لا توجد بيانات متاحة</p>
          </div>
        )}
      </Card>
    </>
  );
};
